/**
 * @file Autocomplete.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Thursday, 11th July 2019 1:46:18 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 * @flow
 */
import type { WaterlineQuery, iDataProvider } from 'web-panel-essentials/types'
import type { CombinedValue } from '../../../types'

import * as React from 'react'
import Portal from 'react-relative-portal'
import debounce from 'lodash/debounce'
import cn from 'classnames'
import { CAST, KEY } from 'web-panel-essentials/misc'
import throttle from 'lodash/throttle'
import { genTestName, __ } from 'web-panel/globals'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faBackspace } from '@fortawesome/free-solid-svg-icons'

library.add(faBackspace)

export type Props = {
  onChange?: (changes: CombinedValue) => void | Promise<void>,
  onBlur?: () => void | Promise<void>,
  onFocus?: () => void | Promise<void>,
  onKeyDown?: (e: {}) => void | Promise<void>,
  comparator?: (a:Object, b:Object) => 0 | 1 | -1,
  testName?: string,
  dataProvider: iDataProvider<any>,
  className?: string,
  placeholder?: string,
  filter?: WaterlineQuery,
  strictMode?: boolean,
  focused?: boolean,
  disabled?: boolean,
  readOnly?: boolean,
  tabIndex?: number,
  invalid?: boolean,
  pending?: boolean
} & CombinedValue

export type State = {
  items: Array<Object>,
  focusedItemIndex: number,
  strictMode: boolean,
  focused: boolean,
  pending: boolean
}

function isElementVisible (el, holder) :boolean {
  holder = holder || document.body
  const { top, bottom, height } = el.getBoundingClientRect()
  const holderRect = holder.getBoundingClientRect()

  return top <= holderRect.top
    ? holderRect.top - top < height
    : bottom - holderRect.bottom < height
}

function scrollAlign (element) : void {  
  if (!element) return
  if (!element.parentNode) return
  
  if (!isElementVisible(element, element.parentNode)) {
    element.parentNode.scrollTo({top: element.offsetTop + 1, behavior: 'smooth'});
  }
}

const scrollAlingDebounced = debounce(scrollAlign, 10)

class Item extends React.PureComponent<{value: mixed, displayValue: string, focused: boolean, onClick: Function}> {
  ref: React.ElementRef<any>

  constructor () {
    super()
    this.ref = React.createRef()
  }

  handleClick = () => {
    const { value, displayValue } = this.props
    this.props.onClick(value, CAST.String(displayValue))
  }

  componentDidUpdate () {
    if (this.props.focused) scrollAlingDebounced(this.ref.current)
  }

  render () : React.Node {
    const { focused, displayValue } = this.props
    const className = cn('item', { focused })
    return (
      <div ref={this.ref} onClick={this.handleClick} className={className}>
        {displayValue}
      </div>
    )
  }
}

class Autocomplete<P: Props, S: State> extends React.Component<P, S> {
  valueKey: string
  displayValueKey: string
  isTouch: boolean
  input: React.ElementRef<any>
  container: React.ElementRef<any>
  activeItem: React.ElementRef<any>
  lastTerm: string

  focus: Function
  renderItem: Function
  resolveItems: Function
  handleBodyClick: Function
  handleBlur: Function
  handleFocus: Function
  activate: Function
  handleClick: Function
  triggerChange: Function
  handleClickOutside: Function
  handleChange: Function
  handleKeyDown: Function
  triggerChangeThrottled: Function
  handleActivateByValues: Function
  handleReset: Function
  dropDownActive: Function

  constructor (props: P) {
    super(props)
    this.container = React.createRef()
    this.input = React.createRef()
    this.resolveItems = debounce(this._resolveItems.bind(this), 300)
    this.state = {
      ...this.state,
      focusedItemIndex: 0,
      items: [],
      strictMode: Boolean(props.strictMode),
      focused: Boolean(props.focused),
      pending: Boolean(props.pending)
    }

    this.renderItem = this._renderItem.bind(this)
    this.handleBodyClick = this._handleBodyClick.bind(this)
    this.handleFocus = this._handleFocus.bind(this)
    this.handleBlur = this._handleBlur.bind(this)
    this.activate = this._activate.bind(this)
    this.handleActivateByValues = this.activateByValues.bind(this)
    this.handleClick = this._handleClick.bind(this)
    this.triggerChange = this._triggerChange.bind(this)
    this.handleReset = this._handleReset.bind(this)
    this.triggerChangeThrottled = throttle(this._triggerChange, 24)

    this.focus = this._focus.bind(this)
    this.handleClickOutside = this._handleClickOutside.bind(this)
    this.handleChange = this._handleChange.bind(this)
    this.handleKeyDown = this._handleKeyDown.bind(this)
    this.dropDownActive = this._dropDownActive.bind(this)
  }

  componentDidMount () {
    this.initMapping()
    if (this.props.focused) this.input.focus()
  }

  shouldComponentUpdate (nextProps: Props, nextState: State) : boolean {
    if (!isEqual(this.props.filter, nextProps.filter)) return true
    if (this.props.value !== nextProps.value) return true
    if (this.props.displayValue !== nextProps.displayValue) return true
    if (this.props.disabled !== nextProps.disabled) return true
    if (this.props.className !== nextProps.className) return true
    if (this.props.focused !== nextProps.focused) return true
    if (this.props.invalid !== nextProps.invalid) return true
    if (this.props.pending !== nextProps.pending) return true
    if (this.props.readOnly !== nextProps.readOnly) return true
    if (this.props.tabIndex !== nextProps.tabIndex) return true
    
    if (this.state.focusedItemIndex !== nextState.focusedItemIndex) return true

    if (!isEqual(this.state.items, nextState.items)) return true
    if (this.state.strictMode !== nextState.strictMode) return true
    if (this.state.focused !== nextState.focused) return true
    if (this.state.pending !== nextState.pending) return true

    return false
  }

  // DataProvider can dictate which keys must be used for value/displayValue
  // We must obey (
  async initMapping () : Promise<void> {
    this.setState({ pending: true })
    this.valueKey = 'value'
    this.displayValueKey = 'value'

    if (this.props.dataProvider) {
      const metadata = await this.props.dataProvider.meta()
      this.valueKey = metadata.value || 'value'
      this.displayValueKey = metadata.displayValue || 'value'
    }
    this.setState({ pending: false })
  }

  _triggerChange (values: CombinedValue) {
    this.props.onChange && this.props.onChange(values)
  }

  _dropDownActive () : boolean {
    return this.state.items.length > 0
  }

  _handleBodyClick (e: any) {
    const container = this.container
    if (e.type === 'touchend') this.isTouch = true
    if (e.type === 'click' && this.isTouch) return
    if (container && (container.contains && !container.contains(e.currentTarget))) this.handleClickOutside()
  }

  _handleClickOutside () {
    if (this.state.focused) {
      if (!this.props.onBlur) this.handleBlur() // Не актуально для вложенных компонент
      if (this.input !== document.activeElement) this.setState({ focused: false })
    }

    if (this.dropDownActive()) this.setState({ items: [] })
  }

  async handleFilterChange (filter: WaterlineQuery) {
    // STUB: Implemented in ReferenceSelect, Select
  }

  async _handleChange (e: any) : Promise<void> {
    const term = e.target.value
    this.triggerChange({ value: null, displayValue: term })
    if (term !== this.lastTerm) {
      this.lastTerm = term
      await this.resolveItems(term)
    }
  }

  async _resolveItems (term: string) : Promise<void> {
    if (!this.props.dataProvider) return

    let { items } = this.state
    const filter = this.props.filter || { }

    if (!term) return this.setState({ items: [] })

    this.setState({ pending: true })
    items = await this.props.dataProvider.get({
      where: filter.where ? {
        and: [
          {
            [this.displayValueKey]: {
              ilike: term
            }
          },
          filter.where
        ]
      } : {
        [this.displayValueKey]: {
          ilike: term
        }
      },
      select: [this.valueKey, this.displayValueKey],
      limit: 20
    })

    let focusedItemIndex = 0
    if (this.props.comparator) items = items.sort(this.props.comparator)
    if (this.props.value) focusedItemIndex = items.findIndex((item) => item[this.valueKey] === this.props.value)
    if (this.state.focused) await this.setState({ items, focusedItemIndex })

    this.setState({ pending: false })
  }

  async _handleKeyDown (e: any, noTriggerOnKeyDownChange: boolean) : Promise<void> {
    e.persist()
    let { items, focusedItemIndex } = this.state
    const { disabled, onKeyDown } = this.props
    if (disabled) return

    // this part activates only when dropdown opened``
    if (items.length) {
      if (e.keyCode === KEY.BACKSPACE || e.keyCode === 46) {
        this.setState({ items: [], focusedItemIndex: 0 })
        return e
      }

      if (e.keyCode === KEY.TAB && items.length) {
        const suggestion = this.getActiveSuggestion()
        if (suggestion) this.triggerChange(suggestion)
        this.setState({ items: [], focusedItemIndex: 0 })
        return e
      }

      if (e.keyCode === KEY.DOWN || e.keyCode === KEY.UP || e.keyCode === KEY.TAB) {
        if (e.keyCode === KEY.DOWN || (e.keyCode === KEY.TAB && !e.shiftKey)) focusedItemIndex++
        if (e.keyCode === KEY.UP || (e.keyCode === KEY.TAB && e.shiftKey)) focusedItemIndex--

        focusedItemIndex = Math.max(focusedItemIndex, 0)
        focusedItemIndex = Math.min(focusedItemIndex, items.length - 1)

        this.setState({ focusedItemIndex })

        if (!noTriggerOnKeyDownChange) {
          const item = items[focusedItemIndex]
          if (item) {
            this.triggerChangeThrottled({
              value: item[this.valueKey],
              displayValue: CAST.String(item[this.displayValueKey])
            })
          }
        }

        e.preventDefault()
      }

      if (e.keyCode === KEY.ENTER) {
        const item = items[focusedItemIndex]
        if (item) this.activate(item)
        e.preventDefault()
        e.stopPropagation()
      }

      if (e.keyCode === KEY.ESCAPE) {
        this.setState({ items: [], focusedItemIndex: 0 })
        e.preventDefault()
      }
    }

    onKeyDown && onKeyDown(e)
  }

  _handleFocus (e: any) {
    if (!this.state.focused) {
      this.setState({ focused: true })
      this.props.onFocus && this.props.onFocus()
    }
  }

  _handleBlur () {
    if (this.state.focused) {
      if (this.input === document.activeElement) return

      this.setState({ focused: false })
      if (this.dropDownActive()) return
      this.props.onBlur && this.props.onBlur()
    }
  }

  async _activate (item: Object) : Promise<void> {
    await this.setState({
      items: [],
      focusedItemIndex: 0
    })

    this.triggerChange({
      value: item[this.valueKey],
      displayValue: CAST.String(item[this.displayValueKey])
    })
    this.focus()
  }

  async activateByValues (value: string, displayValue: string) {
    await this.setState({
      items: [],
      focusedItemIndex: 0
    })

    this.triggerChange({ value, displayValue })
    this.focus()
  }

  _handleClick () {
    this.focus()
  }

  _focus () {
    this.input.focus()
    this.setState({ focused: true })
  }

  _renderItem (item: Object, index: number) : React.Node {
    const value = item[this.valueKey]
    const displayValue = item[this.displayValueKey]
    const focused = index === this.state.focusedItemIndex

    return (
      <Item
        key={value}
        value={value}
        displayValue={displayValue}
        focused={focused}
        onClick={this.handleActivateByValues}
      />
    )
  }

  async _handleReset () {
    this._triggerChange({ value: null, displayValue: '' })
    this.props.onBlur && this.props.onBlur()
  }

  componentDidUpdate () : void {
    if (this.activeItem) scrollAlingDebounced(this.activeItem)
    const suggestion = this.getActiveSuggestion()
    if (suggestion && suggestion.displayValue.length) {
      this.input.focus()
      this.input.setSelectionRange(CAST.String(this.props.displayValue).length, suggestion.displayValue.length)
    }
  }

  getActiveSuggestion () : CombinedValue | null {
    const { items, focusedItemIndex } = this.state
    const item = items[focusedItemIndex]
    if (item) {
      const term = CAST.String(this.props.displayValue).toLowerCase()
      const displayValue = CAST.String(item[this.displayValueKey])
      const lowered = displayValue.toLowerCase()
      if (lowered && lowered.startsWith(term)) { // && lowered !== term
        return {
          displayValue,
          value: item[this.valueKey]
        }
      }
    }
    return null
  }

  render () : React.Node {
    const { items = [], focused } = this.state
    let { className, placeholder, disabled, invalid, displayValue, testName, readOnly, tabIndex, value } = this.props

    const suggestion = this.getActiveSuggestion()
    displayValue = suggestion ? suggestion.displayValue : displayValue

    return (
      <div
        className={cn(className, 'field', 'field-autocomplete', { focused, disabled, invalid })}
        ref={(ref) => (this.container = ref)}
        title={placeholder}
        onClick={this.handleClick}
      >
        <Portal top={15} left={-2} onOutClick={this.handleBodyClick}>
          <div className={cn('field-dropdown', { hidden: !items.length })}>
            {items.map(this.renderItem)}
          </div>
        </Portal>

        <div className='field-data'>
          <input
            {...genTestName(testName)}
            readOnly={readOnly}
            disabled={disabled}
            ref={(input) => { this.input = input }}
            placeholder={placeholder}
            className={cn('field-input', items.length ? 'protect-from-shortcuts' : '', { resolved: !isNil(value) })}
            value={displayValue}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus}
            tabIndex={tabIndex}
            onBlur={this.handleBlur}
          />
          <FontAwesomeIcon
            title={__('RESET')}
            icon='backspace'
            className={cn('reset-icon', { active: Boolean(displayValue) })}
            onClick={this.handleReset}
          />
        </div>
      </div>
    )
  }
}

export default Autocomplete
